import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
} from "@mui/material";
import { AddCircleOutlineRounded } from "@mui/icons-material";
import { keyframes } from "@mui/system";
import { useNavigate } from "react-router-dom";
import { adminService } from "../services/adminService";
import {
  ROUTES,
  buttonGradient,
  buttonGradientHover,
  buttonShadow,
} from "../../../constants/consts";

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const EMPTY = {
  name: "",
  sunucuKodu: "",
  tc: "",
  vergiNo: "",
  phone: "",
  email: "",
  contract: "no",
};

export default function AdminCompanyAdd() {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [snack, setSnack] = useState({ open: false, msg: "" });

  const handleChange = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.sunucuKodu.trim()) {
      setError("Firma adı ve sunucu kodu zorunludur.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await adminService.addCompany(form);
      setSnack({ open: true, msg: "Sunucu başarıyla eklendi." });
      setForm(EMPTY);
      setTimeout(() => navigate(ROUTES.ADMIN_COMPANIES), 900);
    } catch (err) {
      console.error(err);
      setError("Sunucu eklenemedi.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        gap: 1.5,
      }}
    >
      {/* Başlık */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexShrink: 0,
          animation: `${fadeInUp} 0.4s ease-out both`,
        }}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255,255,255,0.14)",
            border: "1px solid rgba(255,255,255,0.20)",
          }}
        >
          <AddCircleOutlineRounded sx={{ color: "#fff", fontSize: 20 }} />
        </Box>
        <Box>
          <Typography
            variant="h6"
            fontWeight={800}
            color="#fff"
            sx={{ fontSize: 18 }}
          >
            Yeni Sunucu Ekle
          </Typography>
          <Typography
            sx={{
              color: "rgba(255,255,255,0.7)",
              fontSize: 11.5,
              fontWeight: 600,
            }}
          >
            Yeni bir firma ve sunucu kodu tanımlayın
          </Typography>
        </Box>
      </Box>

      {/* Form kartı */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          justifyContent: "center", // ← yatayda ortala
          alignItems: "flex-start",
          overflowY: "auto",
          pt: 1,
          pb: 2,
        }}
      >
        <Paper
          elevation={0}
          component="form"
          onSubmit={handleSubmit}
          sx={{
            p: { xs: 2.5, sm: 4 }, // ← iç boşluk arttı
            borderRadius: 3,
            backgroundColor: "rgba(255,255,255,0.98)",
            boxShadow: "0 12px 32px rgba(0,0,0,0.20)",
            border: "1px solid rgba(255,255,255,0.30)",
            width: "100%", // ← parent'ı doldur
            maxWidth: { xs: "100%", sm: 900, md: 1100 }, // ← büyüdü
            animation: `${fadeInUp} 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.1s backwards`,
          }}
        >
          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}
          >
            <TextField
              label="Firma Adı *"
              value={form.name}
              onChange={handleChange("name")}
              fullWidth
              size="small"
            />
            <TextField
              label="Sunucu Kodu *"
              value={form.sunucuKodu}
              onChange={handleChange("sunucuKodu")}
              fullWidth
              size="small"
            />
            <TextField
              label="TC No"
              value={form.tc}
              onChange={handleChange("tc")}
              fullWidth
              size="small"
            />
            <TextField
              label="Vergi No"
              value={form.vergiNo}
              onChange={handleChange("vergiNo")}
              fullWidth
              size="small"
            />
            <TextField
              label="Telefon"
              value={form.phone}
              onChange={handleChange("phone")}
              fullWidth
              size="small"
            />
            <TextField
              label="Email"
              value={form.email}
              onChange={handleChange("email")}
              fullWidth
              size="small"
            />
            <TextField
              label="Sözleşme Durumu *"
              value={form.contract}
              onChange={handleChange("contract")}
              select
              fullWidth
              size="small"
              sx={{ gridColumn: { xs: "1", sm: "1 / span 2" } }}
            >
              <MenuItem value="yes">Var</MenuItem>
              <MenuItem value="no">Yok</MenuItem>
              <MenuItem value="new">Yeni Müşteri</MenuItem>
            </TextField>
          </Box>

          <Box
            sx={{
              mt: 3,
              display: "flex",
              gap: 1.5,
              justifyContent: "flex-end",
            }}
          >
            <Button
              type="button"
              variant="outlined"
              onClick={() => navigate(ROUTES.ADMIN_COMPANIES)}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                px: 3,
                transition: "transform 0.2s ease",
                "&:hover": { transform: "translateY(-1px)" },
              }}
            >
              Vazgeç
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                fontWeight: 700,
                px: 4,
                background: buttonGradient,
                boxShadow: buttonShadow,
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                "&:hover": {
                  background: buttonGradientHover,
                  transform: "translateY(-1px)",
                },
                "&:active": { transform: "translateY(0) scale(0.98)" },
                "&:disabled": { background: "#c7c7c7" },
              }}
            >
              {saving ? (
                <CircularProgress size={20} sx={{ color: "#fff" }} />
              ) : (
                "Kaydet"
              )}
            </Button>
          </Box>
        </Paper>
      </Box>
      <Snackbar
        open={snack.open}
        autoHideDuration={2500}
        onClose={() => setSnack({ open: false, msg: "" })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          onClose={() => setSnack({ open: false, msg: "" })}
          sx={{ borderRadius: 2 }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
